import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { api, type Page } from '../lib/api';
import { ErrorNotice } from '../ui/feedback';
import { DataTable, PageHead, Pager, useKeysetPage } from '../ui/table';
import { chestColumns, FilterSearch, useUrlFilter } from './common';
import { paths } from './paths';
import type { AdminChestRow } from './types';

/**
 * Kistenliste (design.md §6): Name · Besitzer · Team · Server · Position · Varianten · aktualisiert.
 * Filter Server und Item als Eingabe, Besitzer/Team kommen über Links aus den Detailseiten (Chips).
 */
export function ChestsPage() {
  const page = useKeysetPage();
  const [server, setServer] = useUrlFilter('server');
  const [item, setItem] = useUrlFilter('item');
  const [ownerId, setOwner] = useUrlFilter('ownerId');
  const [teamId, setTeam] = useUrlFilter('teamId');
  const [area, setArea] = useUrlFilter('area');
  const query = { server, item, ownerId, teamId, area, cursor: page.cursor, limit: page.limit };
  const list = useQuery({
    queryKey: ['admin', 'chests', 'list', query],
    queryFn: () => api.get<Page<AdminChestRow>>('/admin/chests', query),
    placeholderData: keepPreviousData,
  });
  const filtered = Boolean(server || item || ownerId || teamId || area);
  const reset = () => {
    setArea('');
    setServer('');
    setItem('');
    setOwner('');
    setTeam('');
  };

  return (
    <div className="stack">
      <PageHead title="Kisten" />
      <div className="filters">
        <FilterSearch label="Server, z. B. mc.example.net" value={server} onChange={setServer} />
        <FilterSearch label="Item-ID, z. B. minecraft:diamond" value={item} onChange={setItem} />
        <FilterSearch label="Area" value={area} onChange={setArea} />
        {ownerId && <Chip label="Besitzer gefiltert" onClear={() => setOwner('')} />}
        {teamId && <Chip label="Team gefiltert" onClear={() => setTeam('')} />}
        {filtered && (
          <button type="button" className="btn-quiet" onClick={reset}>
            Filter zurücksetzen
          </button>
        )}
      </div>
      {list.isError && <ErrorNotice error={list.error} onRetry={() => list.refetch()} />}
      <DataTable
        label="Kisten"
        rows={list.data?.items}
        loading={list.isPending}
        rowKey={(c) => c.id}
        rowHref={(c) => paths.chest(c.id)}
        empty={
          filtered ? (
            <>
              Keine Kisten für diese Filter.{' '}
              <button type="button" className="btn-quiet inline" onClick={reset}>
                Filter zurücksetzen
              </button>
            </>
          ) : (
            'Noch keine Kisten registriert.'
          )
        }
        columns={chestColumns()}
      />
      <Pager page={page} count={list.data?.items.length ?? 0} nextCursor={list.data?.nextCursor} />
    </div>
  );
}

function Chip({ label, onClear }: { label: string; onClear: () => void }) {
  return (
    <span className="chip">
      {label}
      <button type="button" aria-label={`${label} entfernen`} onClick={onClear}>
        <X aria-hidden />
      </button>
    </span>
  );
}
